import {DashCanvasItemState, DashCanvasModel} from '@xh/hoist/desktop/cmp/dash';
import {runInAction} from '@xh/hoist/mobx';
import {isEqual, sortBy} from 'lodash';
import {SpecModel, SpecReducer} from '../../../cmp/spec/SpecModel';
import {ResearchWidgetKind} from '../../../data/ResearchTypes';
import {AiSpecTarget} from '../../../data/AiSpecAssistTypes';
import {applyOps, DashboardOp, describeDashboard, describeOps, describePresets, normalizeSpec} from './DashboardSpecOps';
import {DASHBOARD_SPEC_VERSION, DashboardCapabilities, DashboardSpec, DashboardWidget} from './DashboardSpecTypes';

const dashboardSpecReducer: SpecReducer<DashboardSpec, DashboardOp, DashboardCapabilities> = {
    applyOps,
    normalizeSpec,
    describeOps,
    replaceOp: spec => ({op: 'replaceSpec', spec})
};

/**
 * The research dashboard as a DashboardSpec, for the AI dashboard assistant - see SpecModel for
 * dispatch and undo.
 *
 * The canvas stays the owner of the dashboard: the user moves, adds and configures widgets on it
 * directly, and it is what ViewManager saves. So the spec is read from the canvas before every
 * change made here, and written back to it after - an undo returns the canvas to how it was just
 * before the assistant's change.
 */
export class DashboardSpecModel extends SpecModel<DashboardSpec, DashboardOp, DashboardCapabilities> {
    private readonly canvas: DashCanvasModel;
    /** Titles of the widgets and ready-made widgets as of the last dispatch - ops name them by id, which means nothing to the user. */
    private titles = new Map<string, string>();
    /** Widgets the last change gave a new purpose to - by their id on the canvas. */
    private purposed: {id: string; purpose: string}[] = [];

    constructor(canvas: DashCanvasModel, capabilities: DashboardCapabilities) {
        super(dashboardSpecReducer, {catalog: [], capabilities, defaultSpec: readCanvas(canvas)});
        this.canvas = canvas;
    }

    /** What the assistant is sent: the widgets as they are now, as briefs, and the canvas and ready-made widgets it can use. */
    buildTarget(): AiSpecTarget {
        this.pull();
        const {columns, rowHeight} = this.capabilities;
        return {
            spec: describeDashboard(this.spec, this.context),
            capabilities: {columns, rowHeightPx: rowHeight, presets: describePresets(this.context)}
        };
    }

    override describeOps(ops: DashboardOp[]): string {
        return describeOps(ops, this.titles);
    }

    /**
     * The widgets the last dispatch added with a purpose, or gave a new one - each to be refined to it
     * by its own assistant.
     */
    get widgetsToRefine(): {id: string; purpose: string}[] {
        return this.purposed;
    }

    //------------------------
    // Implementation
    //------------------------
    /** Before a dispatch, undo or redo: the spec is the canvas as it is now. */
    protected override beforeChange() {
        this.pull();
        this.titles = new Map([...this.capabilities.presets, ...this.spec.widgets].map(it => [it.id, it.title]));
        this.purposes = new Map(this.spec.widgets.map(w => [w.id, w.state?.purpose]));
        this.purposed = [];
    }

    /** After one: show the spec on the canvas, and take back the ids the canvas gave its widgets. */
    protected override afterChange() {
        const ids = canvasIds(this.spec);
        this.purposed = this.spec.widgets
            .filter(w => w.state?.purpose && w.state.purpose !== this.purposes.get(w.id))
            .map(w => ({id: ids.get(w.id), purpose: w.state.purpose}));

        writeCanvas(this.canvas, this.spec);
        this.adoptSpec(readCanvas(this.canvas));
    }

    /** Purposes of the widgets before the change. */
    private purposes = new Map<string, string>();

    private pull() {
        const spec = readCanvas(this.canvas);
        if (!isEqual(spec, this.spec)) this.adoptSpec(spec);
    }
}

//------------------------
// Canvas <-> spec
//------------------------
function readCanvas(canvas: DashCanvasModel): DashboardSpec {
    const widgets: DashboardWidget[] = canvas.layout
        .map(({i, x, y, w, h}) => {
            const view = canvas.viewModels.find(v => v.id === i);
            if (!view) return null;
            return {id: i, kind: view.viewSpec.id as ResearchWidgetKind, title: view.title, layout: {x, y, w, h}, state: toJson(view.viewState) ?? {}};
        })
        .filter(Boolean);

    // By kind and then by the number in the id - the order in which the canvas gives ids out
    return {version: DASHBOARD_SPEC_VERSION, widgets: sortBy(widgets, [w => w.kind, w => idNumber(w.id)])};
}

/**
 * Load the spec into the canvas. The canvas names its widgets by kind and position in the list, and
 * keeps the widgets whose name is still in use - their state is replaced, and they rebuild from it.
 */
function writeCanvas(canvas: DashCanvasModel, spec: DashboardSpec) {
    const items: DashCanvasItemState[] = spec.widgets.map(w => ({viewSpecId: w.kind, title: w.title, layout: {...w.layout}, state: toJson(w.state)}));

    runInAction(() => {
        canvas.loadState(items);

        // The canvas doesn't publish a layout it was loaded with as a change of its state - but this one
        // is a change, to be saved with the dashboard
        canvas.state = canvas.layout
            .map(({i, x, y, w, h}) => {
                const view = canvas.viewModels.find(v => v.id === i);
                return view ? {layout: {x, y, w, h}, viewSpecId: view.viewSpec.id, title: view.title, state: view.viewState} : null;
            })
            .filter(Boolean);
    });
}

/** The id the canvas will give each widget of the spec: kind and position among the widgets of that kind. */
function canvasIds(spec: DashboardSpec): Map<string, string> {
    const counts = new Map<string, number>();
    return new Map(
        spec.widgets.map(w => {
            const n = counts.get(w.kind) ?? 0;
            counts.set(w.kind, n + 1);
            return [w.id, `${w.kind}_${n}`];
        })
    );
}

function idNumber(id: string): number {
    const n = Number(id.slice(id.lastIndexOf('_') + 1));
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

/** Plain JSON - saved state read back from a dashboard has lost its undefined properties, so compare without them. */
function toJson<T>(v: T): T {
    return v == null ? null : JSON.parse(JSON.stringify(v));
}
